import React from 'react';
import { CheckIcon, GiftIcon, WrenchIcon } from 'lucide-react';
import { SectionHeading } from '../SectionHeading';
import { CheckoutButton } from '../CheckoutButton';
import { COMMERCIAL_RIGHTS, HERO_MOCKUP, OFFER_FEATURES, PRODUCT, VOLUMES } from '../../data/welding';
import { PRICE } from '../../data/checkout';
const PER_PROJECT = `US$${(PRICE.value / 30).toFixed(2)}`;

/**
 * Value stack + first price reveal (#offer). The hero CTA scrolls here.
 * `#offer-price` and `#offer-cta` drive the mobile sticky bar.
 */
export function OfferSection() {
  return <section id="offer" aria-labelledby="offer-title" className="blueprint-grid bg-charcoal py-14 sm:py-20 lg:py-24" data-id="element-188" data-mp="1on7evv6orr3">
      <div className="mx-auto w-full max-w-5xl px-5 sm:px-8" data-id="element-189" data-mp="3nxy7uwm45hyp">
        <SectionHeading id="offer-title" align="center" title="GET THE COMPLETE BACKYARD PLAN COLLECTION" data-id="element-190" data-mp="3nzj18iwoyuuz" />

        <div className="mt-8 grid grid-cols-1 overflow-hidden rounded-[14px] bg-surface shadow-[0_40px_80px_-40px_rgba(0,0,0,0.9)] ring-1 ring-arc/25 sm:mt-10 lg:grid-cols-2" data-id="element-191" data-mp="3j0pm6m1ravsp">
          <div className="hidden items-center justify-center border-r border-white/10 p-8 lg:flex" data-id="element-192" data-mp="1aqfsuea8o22m">
            <img src={HERO_MOCKUP.src} alt={HERO_MOCKUP.alt} width={HERO_MOCKUP.width} height={HERO_MOCKUP.height} loading="lazy" decoding="async" className="max-h-[520px] w-auto max-w-full rounded-[8px] object-contain drop-shadow-[0_24px_30px_rgba(0,0,0,0.5)]" data-id="element-193" data-mp="1fkbkz03tmupv" />
          </div>

          <div className="px-5 pb-7 pt-6 sm:px-10 sm:pb-10 sm:pt-9" data-id="element-194" data-mp="1yga5ovuk5qmv">
            <p className="wordmark text-[1.55rem] text-white sm:text-[1.9rem]" data-id="element-195" data-mp="1kid5lpgwb23g">
              {PRODUCT.wordmarkA} {PRODUCT.wordmarkB}
              <sup className="ml-0.5 font-sans text-[0.45em] font-semibold" data-id="element-196" data-mp="1ohuaohsetx83">™</sup>
            </p>
            <p className="mt-2 font-heading text-[1.2rem] font-semibold uppercase tracking-[0.03em] text-orange" data-id="element-197" data-mp="913mx55lce37">
              30 backyard projects across 6 digital volumes
            </p>

            <ul className="mt-5 grid grid-cols-6 gap-1.5 lg:hidden" aria-label="The six volumes" data-id="element-198" data-mp="2fpm4ygew2n61">
              {VOLUMES.map(volume => <li key={volume.number} data-id="element-199" data-mp="2au6ulyg3b9ed">
                  <img src={volume.src} alt={volume.alt} width={volume.width} height={volume.height} loading="lazy" decoding="async" className="aspect-square w-full rounded-[3px] object-contain ring-1 ring-white/10" data-id="element-200" data-mp="260cf777ipgl3" />
                </li>)}
            </ul>

            <ul className="mt-5 grid gap-3" data-id="element-201" data-mp="1n2rumcbsd46a">
              {OFFER_FEATURES.map(feature => <li key={feature} className="flex items-start gap-3 text-[1rem] font-medium text-white" data-id="element-202" data-mp="1i4q4j7fy04lb">
                  <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-orange text-charcoal" aria-hidden data-id="element-203" data-mp="3hfhqv0pmmpgu">
                    <CheckIcon className="h-3 w-3" strokeWidth={3} data-id="element-204" data-mp="3kxyitdf3xcyj" />
                  </span>
                  {feature}
                </li>)}
            </ul>

            <p className="mt-5 flex items-center gap-3 rounded-[8px] bg-arc/10 px-4 py-3 text-[1rem] font-semibold text-white ring-1 ring-arc/35" data-id="element-205" data-mp="3trtmeh0kyvc2">
              <GiftIcon className="h-5 w-5 shrink-0 text-arc-light" aria-hidden data-id="element-206" data-mp="31zcelpoan5qe" />
              Plus all 5 bonus resources
            </p>

            <p className="mt-3 flex items-start gap-3 rounded-[8px] bg-arc/10 px-4 py-3 ring-1 ring-arc/35" data-id="element-207" data-mp="361zb43vwtetx">
              <WrenchIcon className="mt-0.5 h-5 w-5 shrink-0 text-arc-light" aria-hidden data-id="element-208" data-mp="3az98q6tgcwsu" />
              <span data-id="element-209" data-mp="3g0594aeli0ti">
                <span className="label-tech block text-arc-light" data-id="element-210" data-mp="3g0or1r48wp7j">{COMMERCIAL_RIGHTS.label}</span>
                <span className="mt-1 block text-[0.92rem] leading-snug text-white" data-id="element-211" data-mp="q9a15ygbkpqr">{COMMERCIAL_RIGHTS.sentence}</span>
              </span>
            </p>

            <div id="offer-price" className="mt-6 border-t border-white/10 pt-6 text-center" data-id="element-212" data-mp="245bdu7v3ohao">
              <p className="flex items-baseline justify-center gap-3 whitespace-nowrap" data-id="element-213" data-mp="1z707w87xeqxq">
                <span className="sr-only" data-id="element-214" data-mp="2d69ld04qz6y0">Regular price </span>
                <s className="font-heading text-[1.9rem] font-semibold leading-none text-steel-light decoration-steel-light decoration-2 sm:text-[2.2rem]" data-id="element-215" data-mp="2d7t3yxdvlu2y">
                  {PRICE.original}
                </s>
                <span className="sr-only" data-id="element-216" data-mp="2n2kyttg2q2u1">, now </span>
                <span className="font-heading text-[4.2rem] font-bold leading-none tracking-[-0.01em] text-white sm:text-[4.8rem]" data-id="element-217" data-mp="2i4ji3f64eflq">
                  {PRICE.label}
                </span>
              </p>
              <p className="mt-2 font-heading text-[1.1rem] font-semibold uppercase tracking-[0.06em] text-orange" data-id="element-218" data-mp="2rwgv8rgovhn3">
                Save 50% — Launch Price
              </p>
              <p className="mt-1 text-[0.95rem] text-steel-pale" data-id="element-219" data-mp="30wm1gya5c9zo">{PRICE.note}</p>
              <p className="mx-auto mt-3 inline-flex flex-wrap justify-center gap-x-1.5 rounded-[8px] bg-arc/10 px-3.5 py-2 text-[0.95rem] font-semibold leading-snug text-white ring-1 ring-arc/35" data-id="element-220" data-mp="3khvzmv404yse">
                <span data-id="element-221" data-mp="3ajpywgtxlyp4">30 backyard projects.</span>
                <span data-id="element-222" data-mp="3aiwqzcj8l35e">{PRICE.label} total.</span>
                <span className="text-orange" data-id="element-223" data-mp="3fissn54goihs">Just {PER_PROJECT} per project.</span>
              </p>
              <p className="mt-1.5 text-[0.8rem] text-steel-light" data-id="element-224" data-mp="31recd61xuvef">
                Average per project within the complete collection.
              </p>
              <p className="mx-auto mt-2 max-w-[34ch] text-[0.9rem] leading-snug text-steel-light" data-id="element-225" data-mp="36jnxm8s3z7ad">
                Receive all 6 PDF volumes and 5 bonus resources by email immediately after payment.
              </p>
            </div>

            <CheckoutButton id="offer-cta" label="GET THE COMPLETE COLLECTION" size="lg" className="mt-6 w-full" data-id="element-226" data-mp="2wpysvifeswy5" />

            <p className="mt-4 text-center text-[0.86rem] leading-relaxed text-steel-light" data-id="element-227" data-mp="2mtvo04t6ak7i">
              Digital plans for building outdoor metal projects. Steel, tools and equipment are not included.
            </p>
          </div>
        </div>
      </div>
    </section>;
}